import {
    IsChildConsumerUser,
    IsPremiumConsumerUser,
    IsShadowMailboxUser,
    embargoedMarkets,
    getAdMarketPublishGroupCode,
} from './sharedAdsUtils';

import NativeAdPlacement from './NativeAdPlacement';
import consumerAdsExperimentModeSetValue from './utils/consumerAdsExperimentModeSetValue';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { isConsumer } from 'owa-session-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { updateConsumerAdsExperimentMode } from './utils/updateConsumerAdsExperimentMode';
import { emailadsOptInStore } from 'owa-emailadsoptin-store';
import isEUEmailAdsPolicyMarket from './isEUEmailAdsPolicyMarket';
import isEUEmailAdsPolicyFeatureEnabled from './isEUEmailAdsPolicyFeatureEnabled';
import getCachedEUEmailPolicyOptInBit from './getCachedEUEmailPolicyOptInBit';

let nativeAdEnableOrNot: boolean;
let nativeAdInitialPlacement: NativeAdPlacement;

let userMarket: string;

export function getNativeAdPlacement(): NativeAdPlacement {
    let nativeAdSessionNonChangePlacement = getNativeAdInitialPlacement();

    // If the native ad initial placement is none, we do not need to check EU Ads policy as the Ad is disabled anyway
    if (nativeAdSessionNonChangePlacement == NativeAdPlacement.None) {
        return NativeAdPlacement.None;
    }

    // The following properties could change in one OWA session
    if (isEUEmailAdsPolicyMarket() && isEUEmailAdsPolicyFeatureEnabled()) {
        let euNativeAdPlacement = NativeAdPlacement.None;

        if (emailadsOptInStore.emailAdsOptInBit == 2) {
            euNativeAdPlacement = NativeAdPlacement.OtherPrefer;
        } else if (
            emailadsOptInStore.emailAdsOptInBit == -1 &&
            getCachedEUEmailPolicyOptInBit() == 2
        ) {
            euNativeAdPlacement = NativeAdPlacement.OtherPrefer;
        }

        return euNativeAdPlacement;
    }

    return nativeAdSessionNonChangePlacement;
}

/*
Get the Ad initial placement. The flow is below:
1. Users which are in the following categories do not get native Ads
-- None of the native ads flight is enabled for this user
-- Ad free user
-- Child account
-- User is in a non-supported market
2. If the user is not Focus Inbox enabled, we will show the Ad according to his experimental flight
3. For other users (most set), show the Ad in Other pivot only
*/
export function getNativeAdInitialPlacement(): NativeAdPlacement {
    if (!nativeAdInitialPlacement) {
        if (!getNativeAdEnabledOrNotFromUserProperties()) {
            nativeAdInitialPlacement = NativeAdPlacement.None;
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (38,21): Object is possibly 'undefined'.
            // @ts-expect-error
        } else if (!getUserConfiguration().UserOptions.IsFocusedInboxEnabled) {
            const resetAdShowCount = getInboxNativeAdExpResetCount();

            // if reset count is -1, it means we keep the current behavior. Native ads will be only shown in Other pivot without showing in the primary Inbox
            // If reset count is 1, it means we show native ads in primary Inbox always. We do not need to check the user experiment mode situation
            // For other reset counts, we will determine the native ad position based on the recorded value in the experimentmode.
            if (resetAdShowCount == -1) {
                nativeAdInitialPlacement = NativeAdPlacement.OtherOnly;
            } else if (resetAdShowCount == 1) {
                nativeAdInitialPlacement = NativeAdPlacement.OtherPrefer;
            } else {
                let currentAdShowCount = consumerAdsExperimentModeSetValue(
                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                    // -> Error TS2532 (53,21): Object is possibly 'undefined'.
                    // @ts-expect-error
                    getUserConfiguration().UserOptions.ConsumerAdsExperimentMode
                );
                if (currentAdShowCount == 0 || currentAdShowCount >= resetAdShowCount) {
                    updateConsumerAdsExperimentMode(1);
                    nativeAdInitialPlacement = NativeAdPlacement.OtherPrefer;
                } else if (currentAdShowCount < resetAdShowCount) {
                    const newCount =
                        currentAdShowCount + 1 == resetAdShowCount ? 0 : currentAdShowCount + 1;
                    updateConsumerAdsExperimentMode(newCount);
                    nativeAdInitialPlacement = NativeAdPlacement.OtherOnly;
                }
            }
        } else {
            nativeAdInitialPlacement = NativeAdPlacement.OtherOnly;
        }
    }

    return nativeAdInitialPlacement;
}

/*
Some checks are on the user which will never change during an OWA session
Cache this value so that it will not be calculated again and again
*/
export function getNativeAdEnabledOrNotFromUserProperties(): boolean {
    if (typeof nativeAdEnableOrNot != 'undefined') {
        return nativeAdEnableOrNot;
    }

    nativeAdEnableOrNot = false;

    // Do not log the business user session to decrease the noise in the datapoint
    if (!isConsumer()) {
        return nativeAdEnableOrNot;
    }

    userMarket = getAdMarketPublishGroupCode();

    if (IsPremiumConsumerUser()) {
        return nativeAdEnableOrNot;
    }

    if (IsShadowMailboxUser()) {
        return nativeAdEnableOrNot;
    }

    if (IsChildConsumerUser()) {
        return nativeAdEnableOrNot;
    }

    if (embargoedMarkets.indexOf(userMarket) > -1) {
        return nativeAdEnableOrNot;
    }

    nativeAdEnableOrNot = true;

    return nativeAdEnableOrNot;
}

function getInboxNativeAdExpResetCount(): number {
    // Keep the experiments seperately even if they return the same reset count for easy lookup.
    if (isFeatureEnabled('adsexp-NativePrimaryKeepDisplayEverySession-202111-tr')) {
        return 1;
    } else if (
        isFeatureEnabled('adsexp-DisplayOffNativeOn-treament') &&
        isFeatureEnabled('adsExp-DONO-AdInPrimary-e1')
    ) {
        return 1;
    } else if (isFeatureEnabled('adsExp-NativeInbox-e2-NoDisplayAd')) {
        return 2;
    } else if (
        isFeatureEnabled('adsExp-NativeInbox-e3-NoDisplayAd') ||
        isFeatureEnabled('adsExp-NativeInbox-e3-DisplayAd')
    ) {
        return 3;
    } else if (isFeatureEnabled('adsExp-NativeInbox-e4-NoDisplayAd')) {
        return 4;
    }

    return -1;
}

// This is only used for test
export function resetNativeAdCacheValueForTest() {
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (140,5): Type 'undefined' is not assignable to type 'boolean'.
    // @ts-expect-error
    nativeAdEnableOrNot = undefined;
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (144,5): Type 'undefined' is not assignable to type 'NativeAdPlacement'.
    // @ts-expect-error
    nativeAdInitialPlacement = undefined;
}
